import {
  Audio,
  CompressedFiles,
  Database,
  Document,
  FolderOpen,
  Images,
  Play,
  Presentations,
  Sync,
  ThreeD,
  Vector,
  Video,
} from '@air/next-icons';

export type FilePreview = {
  id: string;
  icon: typeof FolderOpen;
  color: FilePreviewColor;
};

export type FilePreviewColor = {
  backgroundColor: string;
  textColor: string;
};

export type FilePreviewName =
  | 'default'
  | 'doc'
  | 'database'
  | 'compressed'
  | 'presentation'
  | 'audio'
  | '3d'
  | 'other'
  | 'image'
  | 'upload'
  | 'progress'
  | 'video'
  | 'animation';

export const FILE_PREVIEW_MAP: Record<FilePreviewName, FilePreview> = {
  default: {
    id: 'default',
    icon: FolderOpen,
    color: {
      backgroundColor: 'var(--colors-teal3)',
      textColor: 'var(--colors-teal11)',
    },
  },
  doc: {
    id: 'doc',
    icon: Document,
    color: {
      backgroundColor: 'var(--colors-indigo3)',
      textColor: 'var(--colors-indigo10)',
    },
  },
  database: {
    id: 'database',
    icon: Database,
    color: {
      backgroundColor: 'var(--colors-green3)',
      textColor: 'var(--colors-green11)',
    },
  },
  compressed: {
    id: 'compressed',
    icon: CompressedFiles,
    color: {
      backgroundColor: 'var(--colors-yellow3)',
      textColor: 'var(--colors-yellow11)',
    },
  },
  presentation: {
    id: 'presentation',
    icon: Presentations,
    color: {
      backgroundColor: 'var(--colors-orange3)',
      textColor: 'var(--colors-orange11)',
    },
  },
  audio: {
    id: 'audio',
    icon: Audio,
    color: {
      backgroundColor: 'var(--colors-purple3)',
      textColor: 'var(--colors-purple11)',
    },
  },
  '3d': {
    id: '3d',
    icon: ThreeD,
    color: {
      backgroundColor: 'var(--colors-indigo3)',
      textColor: 'var(--colors-indigo11)',
    },
  },
  other: {
    id: 'other',
    icon: Vector,
    color: {
      backgroundColor: 'var(--colors-red3)',
      textColor: 'var(--colors-red11)',
    },
  },
  image: {
    id: 'image',
    icon: Images,
    color: {
      backgroundColor: 'var(--colors-teal3)',
      textColor: 'var(--colors-teal11)',
    },
  },
  upload: {
    id: 'upload',
    icon: Sync,
    color: {
      backgroundColor: 'var(--colors-grey4)',
      textColor: 'var(--colors-grey7)',
    },
  },
  progress: {
    id: 'progress',
    icon: Sync,
    color: {
      backgroundColor: 'var(--colors-grey4)',
      textColor: 'var(--colors-grey7)',
    },
  },
  video: {
    id: 'video',
    icon: Video,
    color: {
      backgroundColor: 'var(--colors-blue3)',
      textColor: 'var(--colors-blue11)',
    },
  },
  animation: {
    id: 'animation',
    icon: Play,
    color: {
      backgroundColor: 'var(--colors-purple3)',
      textColor: 'var(--colors-purple11)',
    },
  },
};

export type FilePreviewExtensionName =
  | '3ds'
  | '3fr'
  | 'ai'
  | 'ari'
  | 'aep'
  | 'aiff'
  | 'ark'
  | 'arw'
  | 'avi'
  | 'bay'
  | 'bitmap'
  | 'blend'
  | 'bmp'
  | 'bz'
  | 'c4d'
  | 'cap'
  | 'collada'
  | 'cr2'
  | 'crw'
  | 'csv'
  | 'dat'
  | 'data'
  | 'db'
  | 'dcr'
  | 'dcs'
  | 'dng'
  | 'drf'
  | 'dbf'
  | 'doc'
  | 'docx'
  | 'dwg'
  | 'eip'
  | 'erf'
  | 'eps'
  | 'exif'
  | 'faw'
  | 'fbx'
  | 'fcp'
  | 'fff'
  | 'fla'
  | 'flac'
  | 'flv'
  | 'gif'
  | 'gpr'
  | 'hdri'
  | 'html'
  | 'iges'
  | 'iiq'
  | 'indb'
  | 'indd'
  | 'jpg'
  | 'jpeg'
  | 'k25'
  | 'kdc'
  | 'key'
  | 'log'
  | 'm4a'
  | 'ma'
  | 'mb'
  | 'mdb'
  | 'mdc'
  | 'mef'
  | 'mel'
  | 'midi'
  | 'mos'
  | 'MOV'
  | 'mov'
  | 'mrw'
  | 'mp3'
  | 'mp4'
  | 'mpg'
  | 'nef'
  | 'nrw'
  | 'numbers'
  | 'obm'
  | 'odp'
  | 'ods'
  | 'odt'
  | 'obj'
  | 'orf'
  | 'pages'
  | 'pef'
  | 'pdf'
  | 'png'
  | 'ppt'
  | 'pptx'
  | 'prproj'
  | 'psb'
  | 'psd'
  | 'ptx'
  | 'pxn'
  | 'raf'
  | 'rar'
  | 'raw'
  | 'riv'
  | 'rtf'
  | 'rw2'
  | 'rwl'
  | 'rwz'
  | 'sav'
  | 'sr2'
  | 'srf'
  | 'srw'
  | 'sql'
  | 'step'
  | 'stl'
  | 'svg'
  | 'tar'
  | 'tif'
  | 'tiff'
  | 'txt'
  | 'vrml'
  | 'wav'
  | 'webp'
  | 'wma'
  | 'wmv'
  | 'x3d'
  | 'x3f'
  | 'xls'
  | 'xlsx'
  | 'xml'
  | 'zip';

export const FILE_PREVIEW_EXTENSION_MAP: Record<FilePreviewExtensionName, FilePreview> = {
  '3ds': FILE_PREVIEW_MAP['3d'],
  '3fr': FILE_PREVIEW_MAP.image,
  aep: FILE_PREVIEW_MAP.default,
  ai: FILE_PREVIEW_MAP.other,
  aiff: FILE_PREVIEW_MAP.audio,
  ari: FILE_PREVIEW_MAP.image,
  arw: FILE_PREVIEW_MAP.image,
  ark: FILE_PREVIEW_MAP.compressed,
  avi: FILE_PREVIEW_MAP.video,
  bay: FILE_PREVIEW_MAP.image,
  bitmap: FILE_PREVIEW_MAP.image,
  blend: FILE_PREVIEW_MAP['3d'],
  bmp: FILE_PREVIEW_MAP.image,
  bz: FILE_PREVIEW_MAP.compressed,
  cap: FILE_PREVIEW_MAP.image,
  c4d: FILE_PREVIEW_MAP['3d'],
  collada: FILE_PREVIEW_MAP['3d'],
  cr2: FILE_PREVIEW_MAP.image,
  crw: FILE_PREVIEW_MAP.image,
  csv: FILE_PREVIEW_MAP.database,
  dat: FILE_PREVIEW_MAP.database,
  data: FILE_PREVIEW_MAP.image,
  dcr: FILE_PREVIEW_MAP.image,
  dcs: FILE_PREVIEW_MAP.image,
  dng: FILE_PREVIEW_MAP.image,
  drf: FILE_PREVIEW_MAP.image,
  db: FILE_PREVIEW_MAP.database,
  dbf: FILE_PREVIEW_MAP.database,
  doc: FILE_PREVIEW_MAP.doc,
  docx: FILE_PREVIEW_MAP.doc,
  dwg: FILE_PREVIEW_MAP['3d'],
  eip: FILE_PREVIEW_MAP.image,
  erf: FILE_PREVIEW_MAP.image,
  eps: FILE_PREVIEW_MAP.other,
  exif: FILE_PREVIEW_MAP.image,
  faw: FILE_PREVIEW_MAP.image,
  fbx: FILE_PREVIEW_MAP['3d'],
  fcp: FILE_PREVIEW_MAP.default,
  fff: FILE_PREVIEW_MAP.image,
  fla: FILE_PREVIEW_MAP.default,
  flac: FILE_PREVIEW_MAP.audio,
  flv: FILE_PREVIEW_MAP.video,
  gif: FILE_PREVIEW_MAP.image,
  gpr: FILE_PREVIEW_MAP.image,
  hdri: FILE_PREVIEW_MAP.image,
  html: FILE_PREVIEW_MAP.doc,
  iges: FILE_PREVIEW_MAP['3d'],
  iiq: FILE_PREVIEW_MAP.image,
  indb: FILE_PREVIEW_MAP.default,
  indd: FILE_PREVIEW_MAP.default,
  jpg: FILE_PREVIEW_MAP.image,
  jpeg: FILE_PREVIEW_MAP.image,
  k25: FILE_PREVIEW_MAP.image,
  kdc: FILE_PREVIEW_MAP.image,
  key: FILE_PREVIEW_MAP.presentation,
  log: FILE_PREVIEW_MAP.doc,
  ma: FILE_PREVIEW_MAP['3d'],
  m4a: FILE_PREVIEW_MAP.audio,
  mb: FILE_PREVIEW_MAP['3d'],
  mdb: FILE_PREVIEW_MAP.database,
  mdc: FILE_PREVIEW_MAP.image,
  mef: FILE_PREVIEW_MAP.image,
  mel: FILE_PREVIEW_MAP['3d'],
  midi: FILE_PREVIEW_MAP.audio,
  mos: FILE_PREVIEW_MAP.image,
  MOV: FILE_PREVIEW_MAP.video,
  mov: FILE_PREVIEW_MAP.video,
  mrw: FILE_PREVIEW_MAP.image,
  mp3: FILE_PREVIEW_MAP.audio,
  mp4: FILE_PREVIEW_MAP.video,
  mpg: FILE_PREVIEW_MAP.video,
  nef: FILE_PREVIEW_MAP.image,
  nrw: FILE_PREVIEW_MAP.image,
  numbers: FILE_PREVIEW_MAP.database,
  obm: FILE_PREVIEW_MAP.image,
  odp: FILE_PREVIEW_MAP.presentation,
  ods: FILE_PREVIEW_MAP.database,
  odt: FILE_PREVIEW_MAP.doc,
  obj: FILE_PREVIEW_MAP['3d'],
  orf: FILE_PREVIEW_MAP.image,
  pages: FILE_PREVIEW_MAP.doc,
  pdf: FILE_PREVIEW_MAP.doc,
  pef: FILE_PREVIEW_MAP.image,
  png: FILE_PREVIEW_MAP.image,
  ppt: FILE_PREVIEW_MAP.presentation,
  pptx: FILE_PREVIEW_MAP.presentation,
  prproj: FILE_PREVIEW_MAP.default,
  psb: FILE_PREVIEW_MAP.image,
  psd: FILE_PREVIEW_MAP.image,
  ptx: FILE_PREVIEW_MAP.image,
  pxn: FILE_PREVIEW_MAP.image,
  raf: FILE_PREVIEW_MAP.image,
  rar: FILE_PREVIEW_MAP.compressed,
  raw: FILE_PREVIEW_MAP.image,
  riv: FILE_PREVIEW_MAP.animation,
  rtf: FILE_PREVIEW_MAP.doc,
  rw2: FILE_PREVIEW_MAP.image,
  rwl: FILE_PREVIEW_MAP.image,
  rwz: FILE_PREVIEW_MAP.image,
  sav: FILE_PREVIEW_MAP.database,
  sql: FILE_PREVIEW_MAP.database,
  step: FILE_PREVIEW_MAP['3d'],
  stl: FILE_PREVIEW_MAP['3d'],
  sr2: FILE_PREVIEW_MAP.image,
  srf: FILE_PREVIEW_MAP.image,
  srw: FILE_PREVIEW_MAP.image,
  svg: FILE_PREVIEW_MAP.other,
  tar: FILE_PREVIEW_MAP.database,
  tif: FILE_PREVIEW_MAP.image,
  tiff: FILE_PREVIEW_MAP.image,
  txt: FILE_PREVIEW_MAP.doc,
  vrml: FILE_PREVIEW_MAP['3d'],
  wav: FILE_PREVIEW_MAP.audio,
  webp: FILE_PREVIEW_MAP.image,
  wma: FILE_PREVIEW_MAP.audio,
  wmv: FILE_PREVIEW_MAP.video,
  x3d: FILE_PREVIEW_MAP['3d'],
  x3f: FILE_PREVIEW_MAP.image,
  xls: FILE_PREVIEW_MAP.database,
  xlsx: FILE_PREVIEW_MAP.database,
  xml: FILE_PREVIEW_MAP.database,
  zip: FILE_PREVIEW_MAP.compressed,
};
